/**
 * The browser end of the Line (spec 4.1). The browser is an audio pipe: it
 * adds the mic track, exchanges its SDP offer through OUR door (the project
 * key never leaves Railway), plays the remote track, and reads the fenced
 * data channel for the few events the ball needs. It sends nothing on the
 * channel; every command is the server's. Peer, mic and fetch are injectable
 * so the whole flow is unit-testable in jsdom.
 */
import type {
  LineMicStats,
  LiveAttachRequest,
  LiveAttachResponse,
  LiveSessionResponse,
  RouterEvent,
} from "@bash/shared";
import { SERVER_URL } from "@/lib/api/base";
import { getOptionalAccessToken } from "@/lib/api/client";
import { isDockWindow } from "@/lib/dock-window";
import { parseSseChunk } from "@/lib/composer/converse-client";
import {
  getMicStream,
  getMicStreamById,
  micDeviceClass,
  micGumCalls,
  type AudioInputInfo,
  type MicDeviceClass,
} from "@/lib/voice/mic";
import { getSharedAudioContext } from "@/lib/voice/shared-audio-context";
import {
  MIC_EVIDENCE_MS,
  MIC_LEVEL_POLL_MS,
  MIC_POLL_MS,
  startSpeechMeter,
  hasSignal,
  micSwapCandidates,
  otherMicClass,
  startMicWatch,
  type MicIdentity,
} from "@/lib/voice/mic-watch";

export type LiveServerEvent = { type: string; [k: string]: unknown };

/** Which rung of the deaf heal worked, for the close line. */
export type HealedRung = "mic" | "redial" | "no";

/**
 * The sent track's OWN settings, the few that shape what the vendor receives (2026-09-21, after
 * the drill: the fake mic was heard 12 of 12, so the difference is the capture). PRIVACY: never
 * `deviceId`, `groupId` or `label` — those name a person; these name a shape.
 */
function trackShape(t: MediaStreamTrack | undefined): LineMicStats["track"] {
  if (!t?.getSettings) return undefined;
  let raw: MediaTrackSettings & { voiceIsolation?: boolean };
  try {
    raw = t.getSettings();
  } catch {
    return undefined;
  }
  const keep = <K extends keyof typeof raw>(k: K) => raw[k];
  const shape = {
    sampleRate: keep("sampleRate"),
    channelCount: keep("channelCount"),
    echoCancellation: keep("echoCancellation"),
    noiseSuppression: keep("noiseSuppression"),
    autoGainControl: keep("autoGainControl"),
    voiceIsolation: raw.voiceIsolation,
  };
  // A browser that reports none of them says nothing, rather than six absents.
  return Object.values(shape).some((v) => v !== undefined) ? shape : undefined;
}

/** A stamp on the performance timeline (`line:<name>`) so a drill can read
 *  click-to-first-word off `performance.getEntriesByType("mark")`. */
const mark = (name: string): void => {
  performance.mark(`line:${name}`);
};

/** One frame off the data channel: a JSON object with a string `type`. */
export function parseLiveEvent(raw: unknown): LiveServerEvent | null {
  if (typeof raw !== "string") return null;
  try {
    const v = JSON.parse(raw) as unknown;
    if (
      typeof v === "object" &&
      v !== null &&
      typeof (v as { type?: unknown }).type === "string"
    ) {
      return v as LiveServerEvent;
    }
    return null;
  } catch {
    return null;
  }
}

interface Deps {
  fetchImpl?: typeof fetch;
  serverUrl?: string;
  token?: string | null;
}

async function bearer(
  deps?: Deps
): Promise<{ url: string; token: string; fetchImpl: typeof fetch }> {
  const token =
    deps?.token !== undefined ? deps.token : await getOptionalAccessToken();
  if (!token) throw new Error("not_signed_in");
  return {
    url: deps?.serverUrl ?? SERVER_URL,
    token,
    fetchImpl: deps?.fetchImpl ?? fetch,
  };
}

/** The door's own refusals, each with copy on the stage. Anything else the
 *  door (or a proxy in front of it) says collapses to the status. */
const DOOR_ERRORS = new Set([
  "live_disabled",
  "live_unconfigured",
  "credits_exhausted",
  "live_create_failed",
]);

/** POST the SDP offer to our door; the answer names the session and the voice. */
export async function exchangeOffer(
  body: {
    sdp: string;
    voice?: string;
    context?: Record<string, unknown>;
    redial?: boolean;
    rollovers?: number;
  },
  deps?: Deps
): Promise<LiveSessionResponse> {
  const { url, token, fetchImpl } = await bearer(deps);
  // The shell's Dock window is the native host (spec 2026-09-22 §5); the web
  // and the shell's main window are the web one, as today.
  const native = isDockWindow();
  const res = await fetchImpl(`${url}/api/composer/live/session`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    },
    // The line stays on (T8b): every native open declares this page can
    // redial a rolled-over line, so the server never rolls over an older Dock
    // page that cannot. The web's body is today's.
    body: JSON.stringify({
      ...body,
      host: native ? "native" : "web",
      ...(native ? { canRollover: true } : {}),
    }),
  });
  if (!res.ok) {
    // The structured field first: an infrastructure 503 is not "voice is off",
    // and a 502 from OpenAI's create is its own story. A body that is not
    // JSON (a proxy page) has no code to read.
    const parsed = (await res.json().catch(() => null)) as {
      error?: unknown;
    } | null;
    const code = parsed?.error;
    if (typeof code === "string" && DOOR_ERRORS.has(code))
      throw new Error(code);
    throw new Error(`line_failed:${res.status}`);
  }
  return (await res.json()) as LiveSessionResponse;
}

/** Ask the server to close the line gracefully. Best effort: the server's idle timer catches a lost request. */
export async function closeLine(sessionId: string, deps?: Deps): Promise<void> {
  const { url, token, fetchImpl } = await bearer(deps);
  await fetchImpl(
    `${url}/api/composer/live/${encodeURIComponent(sessionId)}/close`,
    {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
    }
  ).catch(() => undefined);
}

/**
 * The situation follows the person (spec 2026-09-17 I8): the view and the open
 * task, posted whenever they change while the line is open. Fire-and-forget: a
 * missed update costs one stale sentence, never a wrong navigation.
 */
export async function liveContext(
  sessionId: string,
  context: { view?: string; activeTaskId?: string },
  deps?: Deps
): Promise<void> {
  const { url, token, fetchImpl } = await bearer(deps);
  await fetchImpl(
    `${url}/api/composer/live/${encodeURIComponent(sessionId)}/context`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(context),
    }
  ).catch(() => undefined);
}

const errorEvent = (message: string): RouterEvent => ({
  type: "error",
  code: "internal",
  message,
});

/**
 * A typed ask while the line is open. The server streams the same SSE frames
 * the converse route streams; frames are yielded as they arrive so the inline
 * card fills token by token, exactly as the web composer's own lane does.
 */
export async function* liveText(
  sessionId: string,
  input: string,
  opts?: Deps & {
    signal?: AbortSignal;
    /** This send's request id, and the id of the send it cancelled: the server answers both as one ask. */
    idempotencyKey?: string;
    replaces?: string;
  }
): AsyncGenerator<RouterEvent> {
  const { url, token, fetchImpl } = await bearer(opts);
  let res: Response;
  try {
    res = await fetchImpl(
      `${url}/api/composer/live/${encodeURIComponent(sessionId)}/text`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          input,
          ...(opts?.idempotencyKey
            ? { idempotencyKey: opts.idempotencyKey }
            : {}),
          ...(opts?.replaces ? { replaces: opts.replaces } : {}),
        }),
        signal: opts?.signal,
      }
    );
  } catch (err) {
    yield errorEvent(
      `request failed: ${err instanceof Error ? err.message : String(err)}`
    );
    return;
  }
  if (!res.ok) {
    yield errorEvent(`line text ${res.status}`);
    return;
  }
  let buf = "";
  const reader = res.body?.getReader() ?? null;
  try {
    if (reader) {
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseChunk(buf);
        buf = rest;
        for (const e of events) yield e;
      }
    } else {
      buf = await res.text();
    }
  } catch (err) {
    yield errorEvent(
      `read failed: ${err instanceof Error ? err.message : String(err)}`
    );
    return;
  } finally {
    // A consumer that stops early resumes us here with a return, and the
    // error path above lands here too: release the response either way, so an
    // abandoned typed turn on a metered line never keeps its SSE body open.
    if (reader) await reader.cancel().catch(() => undefined);
  }
  const { events } = parseSseChunk(buf.endsWith("\n\n") ? buf : `${buf}\n\n`);
  for (const e of events) yield e;
}

/** The attach door's codes, which name a refusal rather than say it. */
const ATTACH_CODES = new Set([
  "attachments_required",
  "not_found",
  "line_not_open",
]);

/**
 * The attach door said no. `words` is the server's own sentence where it gave
 * one a person can read as it is (the ten-file cap, a 400); null for a code or
 * a failure with no body, which the stage says in its own words.
 */
export class LineAttachRefused extends Error {
  constructor(
    readonly status: number,
    readonly words: string | null
  ) {
    super(words ?? `line_attach_failed:${status}`);
    this.name = "LineAttachRefused";
  }
}

/**
 * Attach on the call (2026-09-24): files for the line's next ask, spoken or
 * typed, in the same wire the typing box's converse takes. The answer is how
 * many files now wait on the line in all.
 */
export async function liveAttach(
  sessionId: string,
  attachments: LiveAttachRequest["attachments"],
  deps?: Deps
): Promise<LiveAttachResponse> {
  const { url, token, fetchImpl } = await bearer(deps);
  const body: LiveAttachRequest = { attachments };
  const res = await fetchImpl(
    `${url}/api/composer/live/${encodeURIComponent(sessionId)}/attach`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const parsed = (await res.json().catch(() => null)) as {
      error?: unknown;
    } | null;
    const said = parsed?.error;
    throw new LineAttachRefused(
      res.status,
      res.status === 400 && typeof said === "string" && !ATTACH_CODES.has(said)
        ? said
        : null
    );
  }
  return (await res.json()) as LiveAttachResponse;
}

export interface LineHandle {
  sessionId: string;
  voice: string;
  remoteAnalyser: AnalyserNode | null;
  micAnalyser: AnalyserNode | null;
  /** Off the air without leaving the call: the mic tracks stop carrying. */
  setMuted(muted: boolean): void;
  /**
   * What the deaf-call heal needs and only the browser can see (lib/voice/deaf-watch): how long
   * he has been voiced SINCE the greeting ended, how long he has been quiet since his last voiced
   * poll, and the sender's own level. Null where the browser measured none, and absent numbers
   * never dial.
   */
  speech(): {
    voicedMs: number;
    quietMs: number | null;
    sentLevel: number | null;
  };
  /** The server says its greeting is done: the heal counts his voice from here. */
  markGreetingEnd(): void;
  /**
   * THE HEAL'S FIRST RUNG (2026-09-21): put another input on the air, same call, no redial and
   * no greeting. It prefers the OTHER class to the one it is holding, because a microphone that
   * cannot carry him is not healed by the next one of the same kind. The class now on the air, or
   * null when there was nothing to try or it would not take; the label never leaves this module.
   * The speech window starts again on a swap that took.
   */
  swapMic(): Promise<MicDeviceClass | null>;
  /** The door's word: the deaf-call heal is on for this line. The browser reads no flag of its own. */
  deafHeal: boolean;
  /** The door's word: this line takes files for its next ask (attach on the call). Same rule. */
  canAttach: boolean;
  /**
   * The CLOSE evidence line: the whole call's numbers, sent as the line ends
   * and BEFORE the door is told, while the server still has a line to log
   * under. A no-op once it has gone, so no call sends two closes; for a call
   * ended inside its window this is its only line. Optional: a handle that
   * gathers no evidence has none.
   */
  flushMicStats?(healed?: HealedRung): void;
  teardown(): void;
}

export interface OpenLineOpts {
  voice?: string;
  context?: Record<string, unknown>;
  /** This line replaces one the vendor never heard (R-D1): it opens silent, with no greeting. */
  redial?: boolean;
  /** The line stays on (T8b): the rollovers this conversation already spent, for the new line's budget. */
  rollovers?: number;
  onEvent: (e: LiveServerEvent) => void;
  onChannelClosed: () => void;
  createPeer?: () => RTCPeerConnection;
  getMic?: () => Promise<MediaStream>;
  audioEl?: HTMLAudioElement;
  /** null disables the analysers (tests); undefined creates one lazily. */
  audioContext?: AudioContext | null;
  /**
   * Whether this platform's echo canceller hears what the context plays.
   * Undefined reads the browser (contextPlaybackIsEchoCancelled); tests inject.
   */
  contextPlaybackCancelsEcho?: boolean;
  exchange?: typeof exchangeOffer;
  /**
   * The silent-mic watchdog's word (lib/voice/mic-watch.ts): true once no
   * input has signal, false the moment signal comes back. Once per change,
   * and never for a healthy microphone.
   */
  onMicSilent?: (silent: boolean) => void;
  /**
   * The evidence line (2026-09-21): a picture of this call's own mic and
   * uplink, MIC_EVIDENCE_MS in, and one more at the close with the whole
   * call's numbers (or only that one, for a call that ends sooner), for the
   * server's log. No listener, no evidence: nothing is sampled, no timer runs.
   */
  onMicStats?: (stats: LineMicStats) => void;
  /** The inputs a dead mic may be swapped for, and the way one is opened (tests inject both). */
  listMics?: () => Promise<AudioInputInfo[]>;
  getMicById?: (deviceId: string) => Promise<MediaStream>;
}

const ICE_TIMEOUT_MS = 10_000;
/**
 * The first packet fades in over this long, and never longer: a pipeline that
 * starts mid-waveform clicks, and any slower start he hears as latency.
 */
const PLAYBACK_RAMP_S = 0.02;

/** Which pipeline this call's remote audio actually came out of. */
type PlaybackPath = "ctx" | "el" | "fallback";

/**
 * Does the echo canceller hear what an AudioContext plays? Desktop Chrome
 * cancels everything the browser outputs, and Safari's capture cancels its
 * whole process, so the context is safe there. Android is not: its echo
 * canceller is the phone's voice-call path, which only hears audio played on
 * the call itself (the WebRTC element). Played through a context, Bash's voice
 * left the loudspeaker as media, the mic carried it back uncancelled, and the
 * vendor stopped hearing the person (#bugs 2026-09-25, Shubham: prod calls on
 * Android Chrome were heard=no nearly every time; every desktop call was fine).
 */
export function contextPlaybackIsEchoCancelled(nav?: Navigator): boolean {
  if (!nav) return true;
  const hint = (nav as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData?.platform;
  if (hint) return hint !== "Android";
  return !/Android/i.test(nav.userAgent ?? "");
}

/**
 * The speaker, open before the first packet (2026-09-22). An `<audio>` element
 * starts its own decode pipeline when it is handed a source, and the greeting's
 * first word lands inside that start-up; a source node on a RUNNING context
 * outputs from its first sample instead. The gain in between is what the first
 * packet fades in on. Null when the context would not build the graph, and the
 * caller then keeps today's element playback for this call.
 */
function connectPlayback(
  ctx: AudioContext,
  stream: MediaStream
): GainNode | null {
  try {
    const src = ctx.createMediaStreamSource(stream);
    const gain = ctx.createGain();
    src.connect(gain);
    gain.connect(ctx.destination);
    return gain;
  } catch {
    return null;
  }
}

/** A tap on a stream. Both nodes go on the call's `taps`: the context is the
 *  page's and outlives the call, so what this call hangs on it is this call's
 *  to take down. */
function makeAnalyser(
  ctx: AudioContext,
  stream: MediaStream,
  taps: AudioNode[]
): AnalyserNode {
  const src = ctx.createMediaStreamSource(stream);
  const an = ctx.createAnalyser();
  an.fftSize = 512;
  an.smoothingTimeConstant = 0.6;
  src.connect(an);
  taps.push(src, an);
  return an;
}

/** Resolve once the local description carries every candidate; the offer goes over HTTP, so nothing can trickle after it. */
function waitForIce(peer: RTCPeerConnection): Promise<void> {
  if (peer.iceGatheringState === "complete") return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      peer.removeEventListener("icegatheringstatechange", onState);
      reject(new Error("ice_timeout"));
    }, ICE_TIMEOUT_MS);
    function onState() {
      if (peer.iceGatheringState !== "complete") return;
      clearTimeout(timer);
      peer.removeEventListener("icegatheringstatechange", onState);
      resolve();
    }
    peer.addEventListener("icegatheringstatechange", onState);
  });
}

/** What getUserMedia's refusal means for the ball's copy: the person said no,
 *  there is nothing to open, or the device would not start. */
function micErrorCode(err: unknown): string {
  const name =
    typeof err === "object" && err !== null
      ? String((err as { name?: unknown }).name ?? "")
      : "";
  if (name === "NotAllowedError") return "mic_denied";
  if (name === "NotFoundError" || name === "OverconstrainedError")
    return "no_mic";
  return "mic_failed";
}

type Uplink = Pick<
  LineMicStats,
  "packetsSent" | "bytesSent" | "fractionLost" | "rttMs"
>;
const NO_UPLINK: Uplink = {
  packetsSent: null,
  bytesSent: null,
  fractionLost: null,
  rttMs: null,
};
const finite = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/** What the peer knows of its own audio uplink. Null for a number the browser
 *  does not have (zero is a measurement), and never a throw. */
async function uplinkOf(peer: RTCPeerConnection): Promise<Uplink> {
  try {
    const out = { ...NO_UPLINK };
    // Seconds. Ten seconds in, the RTP row often has no round trip yet, while
    // the nominated candidate pair always does (the loopback probe), so the
    // pair's stands in until the far end has reported one.
    const seconds: { rtp: number | null; pair: number | null } = {
      rtp: null,
      pair: null,
    };
    (await peer.getStats()).forEach((s: Record<string, unknown>) => {
      if (s.type === "candidate-pair" && s.nominated === true)
        seconds.pair = finite(s.currentRoundTripTime);
      if ((s.kind ?? s.mediaType) !== "audio") return;
      if (s.type === "outbound-rtp") {
        out.packetsSent = finite(s.packetsSent);
        out.bytesSent = finite(s.bytesSent);
      } else if (s.type === "remote-inbound-rtp") {
        out.fractionLost = finite(s.fractionLost);
        seconds.rtp = finite(s.roundTripTime);
      }
    });
    const rtt = seconds.rtp ?? seconds.pair;
    out.rttMs = rtt === null ? null : Math.round(rtt * 1000);
    return out;
  } catch {
    return NO_UPLINK;
  }
}

/**
 * The browser's OWN level for the track attached to the sender, 0..1, or null where it reports
 * none. `media-source` is the source the sender is reading RIGHT NOW, so after a swap it follows
 * the new input; `outbound-rtp` carries the same number on browsers that put it there instead.
 * This is the one number that says his voice reached the WIRE, not just our analyser's tap.
 */
async function sentLevelOf(peer: RTCPeerConnection): Promise<number | null> {
  try {
    let source: number | null = null;
    let rtp: number | null = null;
    (await peer.getStats()).forEach((s: Record<string, unknown>) => {
      if ((s.kind ?? s.mediaType) !== "audio") return;
      if (s.type === "media-source") source = finite(s.audioLevel);
      else if (s.type === "outbound-rtp") rtp = finite(s.audioLevel);
    });
    return source ?? rtp;
  } catch {
    return null;
  }
}

/**
 * Open the Line: mic in, offer through our door, answer applied, remote audio
 * out. Every failure after the mic is granted stops its tracks and closes the
 * peer before the error reaches the caller; the mic light never stays on for
 * a line that never opened.
 */
export async function openLine(opts: OpenLineOpts): Promise<LineHandle> {
  mark("open");
  const peer = (opts.createPeer ?? (() => new RTCPeerConnection()))();
  const audioEl =
    opts.audioEl ?? Object.assign(new Audio(), { autoplay: true });
  // The PAGE's context (lib/voice/shared-audio-context), warmed in idle time at
  // boot, never one of this call's own: constructing an AudioContext opens the
  // output device, and that block (300 to 470 ms on a real Mac, longer over
  // AirPods) was landing on the ball's click, the one place that cannot afford
  // it. By here it exists, so this line is a lookup.
  const ctx: AudioContext | null =
    opts.audioContext === undefined
      ? getSharedAudioContext()
      : opts.audioContext;
  // Where the echo canceller cannot hear the context (Android), the element
  // plays Bash; the context only feeds the analysers.
  const contextMayPlay =
    opts.contextPlaybackCancelsEcho ??
    contextPlaybackIsEchoCancelled(
      typeof navigator === "undefined" ? undefined : navigator
    );
  // Resumed before the first await, so the context is running by the time the
  // first packet lands — a context resumed later starts suspended under autoplay
  // policy and the analysers read silence. Awaited nowhere: nothing of ours
  // waits on the wake, and a context that exists resumes in a tenth of a ms.
  if (ctx) void ctx.resume().catch(() => undefined);
  const analysers: { remote: AnalyserNode | null; mic: AnalyserNode | null } = {
    remote: null,
    mic: null,
  };
  /** Every analyser tap this call hangs on the shared context, with the source
   *  that feeds it. A swap adds a pair; all of them come down at teardown. */
  const taps: AudioNode[] = [];
  let torn = false;
  // What the evidence line says about this call's downlink: which pipeline
  // played it, and how long the vendor's first packet took after the answer
  // was applied. "el" until a track arrives, because nothing has played yet.
  let playback: PlaybackPath = "el";
  let firstAudioMs: number | null = null;
  let remoteSetAt = 0;
  /** The gain the remote stream plays through; null on the element's path. */
  let gain: GainNode | null = null;
  let ramped = false;
  /** The FIRST packet only: silence to full over PLAYBACK_RAMP_S, so a graph
   *  that starts mid-waveform does not click. Nothing waits on it. */
  const rampIn = (): void => {
    if (!ctx || !gain || ramped) return;
    ramped = true;
    const at = ctx.currentTime;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(1, at + PLAYBACK_RAMP_S);
  };

  // Everything the offer needs, before the mic is asked for. All synchronous,
  // so a throw here is a broken browser rather than a line with a live mic.
  let channel: RTCDataChannel | null = null;
  // Kept for the silent-mic watchdog: a dead input is swapped on the sender,
  // so the line never renegotiates.
  let sender: RTCRtpSender | undefined;
  peer.addEventListener("track", (event: RTCTrackEvent) => {
    if (torn) return;
    // The remote track is born muted and unmutes on the first packet: that
    // is the first sound of Bash, the number the whole click waits for.
    event.track.addEventListener(
      "unmute",
      () => {
        mark("first_audio");
        if (remoteSetAt)
          firstAudioMs = Math.round(performance.now() - remoteSetAt);
        rampIn();
      },
      { once: true }
    );
    const stream = event.streams[0] ?? new MediaStream([event.track]);
    // A context that is not running cannot output, so that call keeps the
    // element and says which path it took.
    gain =
      contextMayPlay && ctx?.state === "running"
        ? connectPlayback(ctx, stream)
        : null;
    if (gain) {
      playback = "ctx";
      mark("playback_ready");
    } else if (ctx && contextMayPlay) {
      playback = "fallback";
      mark("playback_fallback");
    }
    // Chrome only pulls RTP for a remote track attached to a sink, and a
    // muted element is the cheapest sink there is; on the element's own path
    // it is still the one playing.
    audioEl.muted = playback === "ctx";
    audioEl.srcObject = stream;
    void audioEl.play().catch(() => undefined);
    if (ctx) {
      try {
        analysers.remote = makeAnalyser(ctx, stream, taps);
      } catch {
        /* no analyser; the ball breathes without it */
      }
    }
  });

  channel = peer.createDataChannel("oai-events");
  channel.addEventListener("open", () => mark("channel_open"));
  channel.addEventListener("message", (e: MessageEvent) => {
    if (torn) return;
    const ev = parseLiveEvent(e.data);
    if (!ev) return;
    if (ev.type === "session.started") mark("session_started");
    opts.onEvent(ev);
  });
  channel.addEventListener("close", () => {
    if (torn) return;
    opts.onChannelClosed();
  });

  /**
   * The offer needs a sendrecv audio TRANSCEIVER, not a track. With one added
   * here the offer and its candidates are built while the person is still
   * granting the mic, and the granted track rides the same `replaceTrack` the
   * swap ladder uses, so nothing renegotiates. A browser that will not add one
   * before a track keeps today's order for that call: the mic, then the offer.
   */
  let preMic: RTCRtpTransceiver | null = null;
  try {
    preMic = peer.addTransceiver("audio", { direction: "sendrecv" });
    sender = preMic.sender;
  } catch {
    preMic = null;
  }

  const offerAndGather = async (): Promise<void> => {
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    mark("offer_ready");
    // No iceServers, so the peer gathers host candidates only and completes
    // in well under a second (the latency drill measured 126 to 173 ms); the
    // wait keeps the no-trickle design, since the offer goes over HTTP once.
    await waitForIce(peer);
    mark("gathering_complete");
  };

  // The two long poles start together and neither waits on the other. Each is
  // caught up front so a rejection that lands before its await is never an
  // unhandled one; both are rethrown where they belong, below.
  const micReady = (opts.getMic ?? getMicStream)().then((s) => {
    mark("mic_granted");
    return s;
  });
  micReady.catch(() => undefined);
  const gathering = preMic ? offerAndGather() : null;
  gathering?.catch(() => undefined);

  let mic: MediaStream;
  try {
    mic = await micReady;
  } catch (err) {
    // The channel exists by now and the peer's close takes it down: a line
    // that never opened must not report one closing.
    torn = true;
    peer.close();
    throw new Error(micErrorCode(err));
  }
  const teardownMedia = () => {
    for (const t of mic.getTracks()) t.stop();
    peer.close();
    audioEl.srcObject = null;
    // The context outlives every call, so the graph this one hung on it is
    // unwired here — the playback gain and every analyser tap, or a page that
    // takes twenty calls ends up wearing twenty. It is never closed: a closed
    // context cannot be reopened, and the next call would pay the whole device
    // init again.
    gain?.disconnect();
    for (const n of taps) n.disconnect();
  };

  let answer: LiveSessionResponse;
  try {
    if (preMic) {
      // Both ready: the mic on the sender the offer was built around, and the
      // gathered offer. The door is knocked on the moment the later one lands.
      await Promise.all([
        gathering,
        preMic.sender.replaceTrack(mic.getAudioTracks()[0] ?? null),
      ]);
    } else {
      for (const track of mic.getAudioTracks())
        sender = peer.addTrack(track, mic);
      await offerAndGather();
    }
    if (ctx) {
      try {
        analysers.mic = makeAnalyser(ctx, mic, taps);
      } catch {
        /* the bars are decoration, the audio is the point */
      }
    }
    const sdp = peer.localDescription?.sdp;
    if (!sdp) throw new Error("no_offer");
    answer = await (opts.exchange ?? exchangeOffer)({
      sdp,
      voice: opts.voice,
      context: opts.context,
      ...(opts.redial ? { redial: true } : {}),
      ...(opts.rollovers ? { rollovers: opts.rollovers } : {}),
    });
    mark("door_answered");
    await peer.setRemoteDescription({ type: "answer", sdp: answer.sdp });
    mark("remote_set");
    remoteSetAt = performance.now();
  } catch (err) {
    // Closing the peer takes the channel down with it; the caller hears the
    // rejection, never a close for a line that was never open.
    torn = true;
    teardownMedia();
    throw err;
  }

  // The silent-mic watchdog (lib/voice/mic-watch.ts owns the verdict and the
  // numbers). It reads the FLOAT samples off the mic's analyser, so a line
  // with no analyser has no watchdog; it sleeps while the person has the mic
  // muted or the context is not running (an analyser that cannot see reads
  // zeros, and zeros would be a lie); and a healthy mic costs it a look or
  // two, until the first non-zero sample, then nothing for the whole call.
  const used: MicIdentity[] = [];
  const samples = new Float32Array(512);
  let swapped = 0;
  const onTrackEvent = () => watch?.wake();
  const follow = (t: MediaStreamTrack) => {
    const { deviceId, groupId } = t.getSettings();
    used.push({ deviceId, groupId, label: t.label });
    t.addEventListener("mute", onTrackEvent);
    t.addEventListener("ended", onTrackEvent);
  };
  /**
   * Put ONE named input on the air in place of the one the line holds, without renegotiating.
   * The silent-mic watchdog's swap and the deaf heal's swap are the same act, so they are the
   * same code: false when it would not take, and the old stream is kept in that case.
   */
  const putOnAir = async (deviceId: string): Promise<boolean> => {
    used.push({ deviceId });
    const next = await (opts.getMicById ?? getMicStreamById)(deviceId);
    const [track] = next.getAudioTracks();
    try {
      if (torn || !sender || !track) throw new Error("line_gone");
      // Off the air before it is on the wire: a mute is never leaked.
      track.enabled = mic.getAudioTracks()[0].enabled;
      await sender.replaceTrack(track);
      if (torn) throw new Error("line_gone");
    } catch {
      // The mic light never stays on for an input the line does not hold.
      for (const t of next.getTracks()) t.stop();
      return false;
    }
    // One beat: the mute as it stands NOW (they may have pressed it while the
    // peer took the track), the stream the line's own setMuted and teardown act
    // on, the analyser the ball reads.
    const old = mic;
    track.enabled = old.getAudioTracks()[0].enabled;
    mic = next;
    for (const t of old.getTracks()) t.stop();
    try {
      analysers.mic = ctx ? makeAnalyser(ctx, next, taps) : null;
    } catch {
      analysers.mic = null;
    }
    follow(track);
    swapped += 1;
    // A swap that works says nothing to anyone: the timeline is its trace.
    mark("mic_swapped");
    return true;
  };

  const watch = analysers.mic
    ? startMicWatch({
        observe: () => {
          const t = mic.getAudioTracks()[0];
          analysers.mic?.getFloatTimeDomainData(samples);
          return {
            offAir: !t.enabled || ctx?.state !== "running",
            muted: t.muted,
            ended: t.readyState === "ended",
            // No analyser (a rebuild that failed) is no eyes, never a dead mic.
            signal: !analysers.mic || hasSignal(samples),
          };
        },
        candidates: async () =>
          micSwapCandidates(
            await (
              opts.listMics ?? (() => navigator.mediaDevices.enumerateDevices())
            )(),
            used
          ),
        swapTo: putOnAir,
        onSilent: (silent) => {
          mark(silent ? "mic_silent" : "mic_heard");
          opts.onMicSilent?.(silent);
        },
      })
    : null;
  if (watch) follow(mic.getAudioTracks()[0]);

  // The evidence line: the largest sample and the speech level of the window
  // (a look every MIC_POLL_MS, never a frame loop), sent at MIC_EVIDENCE_MS,
  // and once more at the close with the numbers of the WHOLE call — a call
  // that was quiet for its first fifteen seconds and deaf afterwards reads
  // healthy on the first line and deaf on the second.
  // PRIVACY: the device is a CLASS; the label and the ids stay here.
  let micPeak = 0;
  let sentLevel: number | null = null;
  let levelAt = 0;
  let levelReading = false;
  let windowSent = false;
  let closeSent = false;
  let evidence: ReturnType<typeof setInterval> | null = null;
  const speech = startSpeechMeter(MIC_POLL_MS);
  const samplePeak = () => {
    if (!analysers.mic) return;
    analysers.mic.getFloatTimeDomainData(samples);
    for (const v of samples) micPeak = Math.max(micPeak, Math.abs(v));
    speech.add(samples);
  };
  // One reading at a time: a getStats that runs long never queues another.
  const sampleSentLevel = (at: number) => {
    if (levelReading || at - levelAt < MIC_LEVEL_POLL_MS) return;
    levelAt = at;
    levelReading = true;
    void sentLevelOf(peer)
      .then((v) => {
        if (v !== null) sentLevel = Math.max(sentLevel ?? 0, v);
      })
      .finally(() => {
        levelReading = false;
      });
  };
  const sendMicStats = (final: boolean, healed?: HealedRung) => {
    if (!opts.onMicStats || torn || closeSent || (windowSent && !final)) return;
    if (final) {
      closeSent = true;
      if (evidence) clearInterval(evidence);
      evidence = null;
      // One last look: the close may be a poll's breadth after the last tick.
      samplePeak();
    } else windowSent = true;
    const t = mic.getAudioTracks()[0];
    const level = speech.read();
    const seen = {
      // Only a mic that delivered nothing reads 0: any signal is at least 0.001.
      micPeak:
        micPeak === 0 ? 0 : Math.max(0.001, Math.round(micPeak * 1000) / 1000),
      micDead: watch?.dead() ?? false,
      trackMuted: t.muted === true,
      device: micDeviceClass(t.label ?? ""),
      swaps: swapped,
      rms: level.rms,
      voicedMs: level.voicedMs,
      sentLevel:
        sentLevel === null ? null : Math.round(sentLevel * 1000) / 1000,
      ...(final ? { final: true } : {}),
      ...(healed ? { healed } : {}),
      // What this browser actually captured, for the deaf calls.
      gum: micGumCalls(),
      deviceNow: micDeviceClass(t.label ?? ""),
      // The downlink, for the cut first word: which pipeline played this call,
      // and how long the vendor's first packet took after the answer landed.
      playback,
      ...(firstAudioMs === null ? {} : { firstAudioMs }),
      ...(ctx?.sampleRate ? { ctxRate: ctx.sampleRate } : {}),
      ...(trackShape(t) ? { track: trackShape(t) } : {}),
    };
    void uplinkOf(peer).then((uplink) =>
      opts.onMicStats?.({ ...seen, ...uplink })
    );
  };
  if (opts.onMicStats) {
    const liveAt = Date.now();
    evidence = setInterval(() => {
      const at = Date.now();
      samplePeak();
      sampleSentLevel(at);
      if (!windowSent && at - liveAt >= MIC_EVIDENCE_MS) sendMicStats(false);
    }, MIC_POLL_MS);
  }

  return {
    sessionId: answer.sessionId,
    voice: answer.voice,
    deafHeal: answer.deafHeal === true,
    canAttach: answer.canAttach === true,
    get remoteAnalyser() {
      return analysers.remote;
    },
    get micAnalyser() {
      return analysers.mic;
    },
    speech: () => ({
      voicedMs: speech.since().voicedMs,
      quietMs: speech.quietMs(),
      sentLevel,
    }),
    markGreetingEnd: () => speech.mark(),
    swapMic: async () => {
      const prefer = otherMicClass(
        micDeviceClass(mic.getAudioTracks()[0]?.label ?? "")
      );
      // The other class first: a mic that cannot carry him is not healed by the
      // next one of the same kind. A list that will not read is nothing to try.
      const ids = await (async () =>
        micSwapCandidates(
          await (
            opts.listMics ?? (() => navigator.mediaDevices.enumerateDevices())
          )(),
          used,
          prefer
        ))().catch(() => [] as string[]);
      const id = ids[0];
      if (id === undefined) return null;
      if (!(await putOnAir(id).catch(() => false))) return null;
      // The new input starts its own window: what he said into the one that
      // could not carry him is not this one's silence.
      speech.mark();
      return micDeviceClass(mic.getAudioTracks()[0]?.label ?? "");
    },
    flushMicStats: (healed) => sendMicStats(true, healed),
    setMuted(muted: boolean) {
      // The track stays in the peer connection and the analyser keeps its own
      // tap on the stream, so the line never renegotiates and the ball's tide
      // still moves with them; only what the peer hears stops.
      for (const t of mic.getAudioTracks()) t.enabled = !muted;
    },
    teardown() {
      if (torn) return;
      torn = true;
      watch?.stop();
      if (evidence) clearInterval(evidence);
      evidence = null;
      try {
        channel?.close();
      } catch {
        /* already closed */
      }
      teardownMedia();
    },
  };
}
